import { cn } from '@neo/lib/utils'
import { Button } from '@neo/primitives/button'
import { Tooltip } from '@neo/primitives/tooltip'
import { FileIcon } from 'lucide-react'
import { getImageName } from 'v2/pages/settings/whatsapp/templates/create-template.config'
import type { RWhatsappTemplate } from 'v2/types/api/settings'
import type { Chunk, ChunkType } from 'v2/types/chunk.internal'
import { logger } from 'v2/utils/logger'
import { ChunkFooter } from './chunk-footer'

interface TemplateChunkBubbleProps {
  chunk: Chunk<ChunkType.template>
  isPreview?: boolean
}

type TemplateMeta = Chunk<ChunkType.template>['meta']

export const TemplateChunkBubble = ({ chunk, isPreview = false }: TemplateChunkBubbleProps) => {
  const { template } = chunk.meta

  if (!template) {
    logger.error('Template not found:', chunk.meta.templateData.template_uuid)
    return null
  }

  const fillVariables = (text: string, params: Array<{ text?: string } | undefined> = []) =>
    text.replace(/{{(\d+)}}/g, (_, index) => {
      const param = params[parseInt(index, 10) - 1]
      return param?.text || `{{${index}}}`
    })

  const renderHeader = (
    headerComponent: RWhatsappTemplate['data']['components'][0],
    headerParam?: TemplateMeta['templateData']['components'][0]['parameters'][0],
  ) => {
    if (!headerComponent) return null

    switch (headerComponent.format) {
      case 'TEXT':
        return (
          <div className="text-primary text-p-600 mb-2">
            {fillVariables(headerComponent.text || '', headerParam ? [headerParam] : [])}
          </div>
        )
      case 'IMAGE':
        return (
          <div className="mb-2">
            <div
              className={cn(
                'bg-muted flex items-center justify-center rounded mb-2',
                headerParam?.image?.link ? '' : 'h-[150px]',
              )}
            >
              <img
                src={headerParam?.image?.link || (getImageName.IMAGE as unknown as string)}
                alt="Header template"
                className="max-h-full w-full max-w-[304px]"
              />
            </div>
          </div>
        )
      case 'VIDEO':
        return (
          <div className="mb-2">
            <div
              className={cn(
                'bg-muted flex items-center justify-center rounded mb-2',
                headerParam?.video?.link ? '' : 'h-[150px]',
              )}
            >
              {headerParam?.video?.link ? (
                <video src={headerParam?.video?.link} className="max-h-full w-auto" controls>
                  <track kind="captions" />
                </video>
              ) : (
                <img
                  src={getImageName.VIDEO as unknown as string}
                  alt="Default Video Thumbnail"
                  className="max-h-full w-auto"
                />
              )}
            </div>
          </div>
        )
      case 'DOCUMENT':
        return (
          <div className="bg-muted/60 p-4 mb-2 rounded">
            <div className="flex items-center">
              <FileIcon className="w-4 h-4 mr-2" />
              <span className="font-semibold">Document</span>
            </div>
          </div>
        )
      default:
        return null
    }
  }

  const renderBody = (
    bodyComponent: RWhatsappTemplate['data']['components'][0],
    bodyParams?: TemplateMeta['templateData']['components'][0]['parameters'],
  ) => {
    if (!bodyComponent) return null

    return (
      <div className="text-sm whitespace-pre-wrap mb-2">
        {fillVariables(bodyComponent.text || '', bodyParams || [])}
      </div>
    )
  }

  const renderFooter = (footerComponent: RWhatsappTemplate['data']['components'][0]) => {
    if (!footerComponent) return null

    return <div className="text-s-400 text-secondary mb-2">{footerComponent.text}</div>
  }

  const renderButtons = (buttonsComponent: RWhatsappTemplate['data']['components'][0]) => {
    if (!buttonsComponent || !buttonsComponent.buttons) return null

    return (
      <div className="flex flex-col gap-1">
        {buttonsComponent.buttons.map((button) => (
          <Tooltip key={`button-${button.text}`} content="This button is for display purposes only">
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-center text-brand-text border-brand"
              disabled
            >
              {button.text}
            </Button>
          </Tooltip>
        ))}
      </div>
    )
  }

  const headerComponent = template.data.components.find((c) => c.type === 'HEADER')
  const bodyComponent = template.data.components.find((c) => c.type === 'BODY')
  const footerComponent = template.data.components.find((c) => c.type === 'FOOTER')
  const buttonsComponent = template.data.components.find((c) => c.type === 'BUTTONS')

  const headerParam = chunk.meta.templateData.components.find((c) => c.type === 'header')
    ?.parameters[0]
  const bodyParams = chunk.meta.templateData.components.find((c) => c.type === 'body')?.parameters

  return (
    <div
      className={cn(
        'flex flex-col gap-1 ml-auto max-w-xs min-w-60 overflow-hidden',
        isPreview ? 'flex-1 min-w-50 max-w-xl' : '',
      )}
    >
      {isPreview && <div className="text-s-500 mb-1 text-secondary">Message preview</div>}
      <div className="bg-bubble-agent rounded-lg p-2">
        {headerComponent && renderHeader(headerComponent, headerParam)}
        {bodyComponent && renderBody(bodyComponent, bodyParams)}
        {footerComponent && renderFooter(footerComponent)}
        <ChunkFooter
          timetoken={chunk.timetoken}
          state={chunk.meta.state}
          align="right"
          channel={chunk.channel}
          stateReason={chunk.meta.stateReason}
        />
      </div>
      {buttonsComponent && renderButtons(buttonsComponent)}
    </div>
  )
}
